import React, { useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Add, Delete, Edit, Visibility } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useCompanyGroups } from '../hooks';
import { CompanyGroup } from '../services/firebaseFunctions';
import { useAuth } from '../contexts/AuthContext';
import { companyGroupErrorMessage } from '../utils/adminFeedback';
import DestructiveActionDialog from '../components/DestructiveActionDialog';

const noPermissionTooltip = 'Você não tem permissão de edição no módulo Empresas.';

const CompanyGroupsPage: React.FC = () => {
  const { companyGroups, loading, error, create, update, remove } = useCompanyGroups();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canManage = hasPermission('empresas', 'editar');
  const [editing, setEditing] = useState<CompanyGroup | null>(null);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [feedback, setFeedback] = useState<string>();
  const [feedbackSeverity, setFeedbackSeverity] = useState<'success' | 'error'>('error');
  const [formError, setFormError] = useState<string>();
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const [groupPendingDeletion, setGroupPendingDeletion] = useState<CompanyGroup | null>(null);

  const openForm = (group?: CompanyGroup) => {
    setEditing(group || null);
    setName(group?.name || '');
    setDescription(group?.description || '');
    setFormError(undefined);
    setOpen(true);
  };

  const save = async () => {
    if (savingRef.current) return;

    if (name.trim().length < 2) {
      setFormError('Informe um nome com ao menos 2 caracteres.');
      return;
    }
    setFormError(undefined);
    savingRef.current = true;
    setSaving(true);

    try {
      if (editing) {
        await update(editing.id, {
          name: name.trim(),
          description: description.trim() || null,
        });
      } else {
        const normalizedDescription = description.trim();
        await create({
          name: name.trim(),
          ...(normalizedDescription ? { description: normalizedDescription } : {}),
        });
      }
      setOpen(false);
    } catch (caught) {
      setFormError(companyGroupErrorMessage(caught));
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const removeGroup = async () => {
    if (!groupPendingDeletion) return;

    try {
      await remove(groupPendingDeletion.id);
      setFeedback('Grupo excluído com sucesso. As Empresas vinculadas foram desvinculadas.');
      setFeedbackSeverity('success');
    } catch (caught) {
      setFeedback(companyGroupErrorMessage(caught));
      setFeedbackSeverity('error');
    }
    setGroupPendingDeletion(null);
  };

  return <Box>
    <Typography variant="h4" sx={{ mb: 3 }}>Grupos</Typography>
    {(error || feedback) && <Alert severity={feedback ? feedbackSeverity : 'error'} sx={{ mb: 2 }}>{feedback || error}</Alert>}
    <Tooltip title={canManage ? '' : noPermissionTooltip}>
      <span>
        <Button variant="contained" startIcon={<Add />} onClick={() => openForm()} disabled={!canManage} sx={{ mb: 2 }}>Novo Grupo</Button>
      </span>
    </Tooltip>
    <Paper>
      <Table>
        <TableHead><TableRow><TableCell>Nome</TableCell><TableCell>Descrição</TableCell><TableCell align="right">Ações</TableCell></TableRow></TableHead>
        <TableBody>
          {loading ? <TableRow><TableCell colSpan={3}>Carregando…</TableCell></TableRow>
            : companyGroups.length === 0 ? <TableRow><TableCell colSpan={3}>Nenhum Grupo cadastrado.</TableCell></TableRow>
              : companyGroups.map((group) => <TableRow key={group.id}>
                <TableCell>{group.name}</TableCell>
                <TableCell>{group.description || '—'}</TableCell>
                <TableCell align="right">
                  <IconButton aria-label="Ver detalhe do grupo" onClick={() => navigate(`/company-groups/${group.id}`)}><Visibility /></IconButton>
                  <Tooltip title={canManage ? '' : noPermissionTooltip}>
                    <span>
                      <IconButton aria-label="Editar grupo" disabled={!canManage} onClick={() => openForm(group)}><Edit /></IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title={canManage ? '' : noPermissionTooltip}>
                    <span>
                      <IconButton aria-label="Excluir grupo" color="error" disabled={!canManage} onClick={() => setGroupPendingDeletion(group)}><Delete /></IconButton>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>)}
        </TableBody>
      </Table>
    </Paper>
    <Dialog open={open} onClose={() => setOpen(false)} fullWidth>
      <DialogTitle>{editing ? 'Editar Grupo' : 'Novo Grupo'}</DialogTitle>
      <DialogContent>
        {formError && <Alert severity="error" sx={{ mt: 1 }}>{formError}</Alert>}
        <TextField
          autoFocus
          fullWidth
          required
          label="Nome"
          value={name}
          onChange={(event) => {
            setFormError(undefined);
            setName(event.target.value);
          }}
          margin="normal"
          error={Boolean(formError)}
          inputProps={{ maxLength: 100 }}
        />
        <TextField fullWidth label="Descrição" value={description} onChange={(event) => setDescription(event.target.value)} margin="normal" multiline minRows={3} inputProps={{ maxLength: 500 }} />
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setOpen(false)} disabled={saving}>Cancelar</Button>
        <Button variant="contained" onClick={() => void save()} disabled={saving}>
          {saving ? 'Salvando…' : 'Salvar'}
        </Button>
      </DialogActions>
    </Dialog>
    <DestructiveActionDialog
      open={Boolean(groupPendingDeletion)}
      entityLabel="Grupo"
      entityName={groupPendingDeletion?.name || ''}
      onCancel={() => setGroupPendingDeletion(null)}
      onConfirm={() => void removeGroup()}
    />
  </Box>;
};

export default CompanyGroupsPage;
